"use client";

import Image, { StaticImageData } from "next/image";
import { useRef } from "react";
import { useParallaxTilt, useScrollFade } from "@/lib/MotionProvider";
import "@/styles/sections.css";
import intro_thumb from "@/assets/img/update/normal/1.png";
import icon_1 from "@/assets/img/update/icon/feature-icon1-1.svg";
import icon_2 from "@/assets/img/update/icon/feature-icon1-2.svg";
import icon_3 from "@/assets/img/update/icon/feature-icon1-3.svg";
import { useLanguage } from "@/utils/LanguageContext";
import GetInvolvedButton from "@/component/common/GetInvolvedButton";

type VisionStep = {
  id: number;
  icon: StaticImageData;
  titleKey: string;
  descKey: string;
  areaClassName: string;
  toneClassName: string;
};

const visionSteps: VisionStep[] = [
  { id: 1, icon: icon_1, titleKey: "home_how_card_1_title", descKey: "home_how_card_1_body", areaClassName: "vision-step-item--1", toneClassName: "feature-card--process" },
  { id: 2, icon: icon_2, titleKey: "home_how_card_2_title", descKey: "home_how_card_2_body", areaClassName: "vision-step-item--2", toneClassName: "feature-card--process" },
  { id: 3, icon: icon_1, titleKey: "home_how_card_4_title", descKey: "home_how_card_4_body", areaClassName: "vision-step-item--3", toneClassName: "feature-card--process" },
  { id: 4, icon: icon_3, titleKey: "home_how_card_3_title", descKey: "home_how_card_3_body", areaClassName: "vision-step-item--4", toneClassName: "feature-card--outcome" },
  { id: 5, icon: icon_2, titleKey: "home_how_card_5_title", descKey: "home_how_card_5_body", areaClassName: "vision-step-item--5", toneClassName: "feature-card--outcome" },
];

const VisionCard = ({ item }: { item: VisionStep }) => {
  const ref = useRef<HTMLElement>(null);
  const { t } = useLanguage();
  useScrollFade(ref);
  useParallaxTilt(ref);

  return (
    <article ref={ref} className={`feature-card h-100 ${item.toneClassName}`}>
      <div className="feature-card-icon">
        <Image src={item.icon} alt="" aria-hidden="true" loading="lazy" />
      </div>
      <div className="feature-card-details">
        <div className="vision-step-heading">
          <span className="vision-step-number" aria-hidden="true">{item.id}</span>
          <h3 className="feature-card-title">{t(item.titleKey)}</h3>
        </div>
        <p className="feature-card-text">{t(item.descKey)}</p>
      </div>
    </article>
  );
};

const VisionArea = () => {
  const { t } = useLanguage();
  const thumbRef = useRef<HTMLDivElement>(null);
  useScrollFade(thumbRef);
  const processSteps = visionSteps.filter((item) => item.id <= 3);
  const outcomeSteps = visionSteps.filter((item) => item.id > 3);

  return (
    <section
      className="pt-130 overflow-hidden bg-black2 pb-5 home-anchor-target"
      id="VisionArea"
      aria-labelledby="vision-title"
    >
      <div className="container">
        <header className="section-title mb-45 vision-intro">
          <h2 className="title style2" id="vision-title">{t("How it Works")}</h2>
          <p className="sec-text">{t("home_how_intro")}</p>
        </header>

        <div className="vision-step-grid" aria-label={t("How it Works")}>
          <ol className="vision-process-stack">
            {processSteps.map((item) => (
              <li key={item.id} className={`vision-step-item ${item.areaClassName}`}>
                <VisionCard item={item} />
              </li>
            ))}
          </ol>
          <ol className="vision-outcome-stack">
            {outcomeSteps.map((item) => (
              <li key={item.id} className={`vision-step-item ${item.areaClassName}`}>
                <VisionCard item={item} />
              </li>
            ))}
          </ol>
          <div className="vision-logo-panel" aria-hidden="true">
            <div ref={thumbRef} className="intro-thumb1 vision-logo-box alltuchtopdown">
              <Image src={intro_thumb} alt="" />
            </div>
          </div>
        </div>
        <GetInvolvedButton />
      </div>
    </section>
  );
};

export default VisionArea;
